import { formatAreaKmSq } from './formUi';

/**
 * Coverage-area progress view for the project form: the org's max area is the
 * track; existing project areas fill it (accent), and the polygon being drawn
 * adds a highlighted segment (cyan, or red when it pushes the org over budget).
 */
export function QuotaBar({
  usedKmSq,
  pendingKmSq,
  maxKmSq,
}: {
  usedKmSq: number;
  pendingKmSq: number;
  maxKmSq: number | null;
}) {
  const total = usedKmSq + pendingKmSq;
  const over = maxKmSq != null && total > maxKmSq;
  const cap = maxKmSq && maxKmSq > 0 ? maxKmSq : Math.max(total, 1);
  const usedPct = Math.min(100, (usedKmSq / cap) * 100);
  const pendingPct = Math.min(100 - usedPct, (pendingKmSq / cap) * 100);

  return (
    <div
      style={{
        background: 'var(--surface-card)',
        border: `1px solid ${over ? 'rgba(255,93,115,0.5)' : 'var(--border)'}`,
        borderRadius: 10,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <span style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-faint)' }}>
          Coverage area
        </span>
        <span className="mono" style={{ fontSize: 12, color: over ? '#ff5d73' : 'var(--text-secondary)' }}>
          {formatAreaKmSq(total)}
          {maxKmSq != null ? ` / ${formatAreaKmSq(maxKmSq)}` : ''}
        </span>
      </div>
      <div
        style={{
          position: 'relative',
          height: 10,
          borderRadius: 6,
          background: 'rgba(120,160,200,0.12)',
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        <div style={{ width: `${usedPct}%`, background: 'rgba(0,229,255,0.45)' }} />
        <div style={{ width: `${pendingPct}%`, background: over ? '#ff5d73' : '#00e5ff' }} />
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: 'var(--text-dim)' }}>
        <Legend swatch="rgba(0,229,255,0.45)" label={`Existing ${formatAreaKmSq(usedKmSq)}`} />
        <Legend swatch={over ? '#ff5d73' : '#00e5ff'} label={`This project ${formatAreaKmSq(pendingKmSq)}`} />
      </div>
      {over && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#ff5d73' }}>
          Exceeds the organization's max coverage area.
        </div>
      )}
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: swatch }} />
      {label}
    </span>
  );
}
